import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { useStores } from '~/hooks/useStore';
import type { Category } from '~/stores/CategoriesStore';

/** Maximum number of category links shown in the header */
const MAX_CATEGORIES = 5;

const linkClassName =
  'whitespace-nowrap text-navigation-text-primary transition-colors duration-200 first:pl-4 last:pr-4 hover:text-navigation-text-secondary md:first:pl-10 md:last:pr-10';

interface HeaderDubaiExpressCategoriesProps
  extends Omit<React.ComponentPropsWithoutRef<'nav'>, 'children'> {}

/**
 * Horizontal, single-line, scrollable list of category links for the
 * DubaiExpress header. Shows the top-level categories of the shop tree (the
 * roots of "All categories / Browse" in the CMS), with a fallback to home
 * categories while the shop tree is empty.
 */
export const HeaderDubaiExpressCategories =
  observer<HeaderDubaiExpressCategoriesProps>(({ className, ...props }) => {
    const { shopCategories, homeCategories } = useStores();

    const categories: Readonly<Category[]> =
      shopCategories.list.length > 0
        ? shopCategories.list
        : homeCategories.list;

    if (categories.length === 0) return null;

    return (
      <nav
        {...props}
        className={cn(
          'scrollbar-hide -mx-4 flex flex-nowrap items-center justify-start gap-6 overflow-x-auto md:-mx-10 md:justify-center md:gap-10',
          className,
        )}
      >
        {categories.slice(0, MAX_CATEGORIES).map((category) => (
          <Link
            key={category.id}
            to={`/c/${category.id}?showProducts=true`}
            className={linkClassName}
          >
            <Typography.Text300Medium as="span">
              {category.name}
            </Typography.Text300Medium>
          </Link>
        ))}
      </nav>
    );
  });
HeaderDubaiExpressCategories.displayName = 'HeaderDubaiExpressCategories';
